import { create } from 'zustand';
import { LLM_PROVIDERS, type LlmConfig, type LlmProviderId } from '../director/llm-director';

const LLM_CONFIG_KEY = 'cinematic-engine:llm-config';

export interface LlmConfigState extends LlmConfig {
  setProvider: (provider: LlmProviderId) => void;
  setApiKey: (apiKey: string) => void;
  setModel: (model: string) => void;
  clear: () => void;
}

export function loadLlmConfig(raw: string | null): LlmConfig {
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<LlmConfig>;
      if (parsed.provider && LLM_PROVIDERS[parsed.provider]) {
        return {
          provider: parsed.provider,
          apiKey: typeof parsed.apiKey === 'string' ? parsed.apiKey : '',
          model:
            typeof parsed.model === 'string'
              ? parsed.model
              : LLM_PROVIDERS[parsed.provider].defaultModel,
        };
      }
    } catch {
      // ignore corrupt config
    }
  }
  return { provider: 'openai', apiKey: '', model: LLM_PROVIDERS.openai.defaultModel };
}

export const useLlmConfig = create<LlmConfigState>((set, get) => {
  const persist = () => {
    const { provider, apiKey, model } = get();
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(LLM_CONFIG_KEY, JSON.stringify({ provider, apiKey, model }));
    } catch {
      // storage full or unavailable — keep in memory only
    }
  };

  return {
    ...loadLlmConfig(
      typeof localStorage === 'undefined' ? null : localStorage.getItem(LLM_CONFIG_KEY)
    ),
    setProvider: (provider) => {
      set({ provider, model: LLM_PROVIDERS[provider].defaultModel });
      persist();
    },
    setApiKey: (apiKey) => {
      set({ apiKey });
      persist();
    },
    setModel: (model) => {
      set({ model });
      persist();
    },
    clear: () => {
      set({ apiKey: '', model: LLM_PROVIDERS.openai.defaultModel, provider: 'openai' });
      try {
        localStorage.removeItem(LLM_CONFIG_KEY);
      } catch {
        // ignore
      }
    },
  };
});

export function hasLlmConfigured(): boolean {
  return useLlmConfig.getState().apiKey.trim().length > 0;
}

export { LLM_CONFIG_KEY };
